

import { FormInput } from "@/components/Inputs/FormInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { IconText } from "@/components/IconText/IconText";
import { useTranslations } from "next-intl";
import { useChangePasswordForm } from "./useChangePasswordForm";

type ChangePasswordFormProps = {
  token: string;
  expiresAt: number;
};

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  token,
  expiresAt,
}) => {
  const t = useTranslations("changePassword");

  const {
    formData,
    passwordValidation,
    formErrors,
    isLoading,
    handleInputChange,
    handleSubmit,
    prefetchRoute,
    rateLimited,
  } = useChangePasswordForm(token, expiresAt);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {formErrors.form && (
        <p
          role="alert"
          data-testid="change-password-form-error"
          className="font-error body-2-regular text-center mb-4"
        >
          {formErrors.form}
        </p>
      )}

      {["password", "confirmPassword"].map((id) => (
        <FormInput
          key={id}
          id={id}
          className={id === "password" ? "mb-6" : ""}
          type="password"
          value={formData[id as "password" | "confirmPassword"]}
          label={t(id)}
          onChangeAction={(value) =>
            handleInputChange(id as "password" | "confirmPassword", value)
          }
          error={formErrors[id]}
        />
      ))}

      <Spacing height="h-4" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {["length", "uppercase", "lowercase", "specialChar"].map((id) => (
          <IconText
            key={id}
            isValid={
              passwordValidation[
                id as "length" | "uppercase" | "lowercase" | "specialChar"
              ]
            }
            text={t(id)}
            defaultIcon={<IconCircleX size={16} stroke={2} />}
            successIcon={<IconCircleCheck size={16} stroke={2} />}
          />
        ))}
      </div>

      <Spacing height="h-6" />

      <PrimaryButton
        text={t("confirmPasswordButton")}
        size="small"
        type="submit"
        onHover={prefetchRoute}
        disabled={isLoading || rateLimited}
        loading={isLoading}
      />
    </form>
  );
};
